import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AnalyticsCharts from "../components/analytics/AnalyticsCharts";
import RetryBlock from "../components/common/RetryBlock";
import useApiRequest from "../hooks/useApiRequest";
import useShellContext from "../layout/useShellContext";
import { getAnalyticsCalls, getAnalyticsLeads } from "../services/analyticsApi";
import { formatNow } from "../utils/formatters";
import {
  buildDurationBuckets,
  buildLeadTaskSeries,
  buildOutcomeSeries,
  normalizeLeads,
} from "../utils/helpers";

const INITIAL_ANALYTICS_DATA = {
  callsPerDay: { labels: [], values: [] },
  rawCalls: [],
  leads: [],
};

export default function AnalyticsPage() {
  const { registerPageControls, resetPageControls } = useShellContext();
  const { data, error, run } = useApiRequest(INITIAL_ANALYTICS_DATA);
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);
  const mountedRef = useRef(false);

  const loadAnalytics = useCallback(async ({ signal } = {}) => {
    const [callsResponse, leadsResponse] = await Promise.all([
      getAnalyticsCalls({ signal }),
      getAnalyticsLeads({ signal }),
    ]);

    const leads = Array.isArray(leadsResponse)
      ? leadsResponse
      : leadsResponse?.leads || [];

    return {
      callsPerDay: callsResponse?.callsPerDay || { labels: [], values: [] },
      rawCalls: callsResponse?.rawCalls || [],
      leads: normalizeLeads(leads),
    };
  }, []);

  const loadPageData = useCallback(
    async ({ trackRefresh = false } = {}) => {
      if (trackRefresh && mountedRef.current) {
        setShellRefreshing(true);
      }

      try {
        const result = await run(loadAnalytics);

        if (mountedRef.current) {
          setLastUpdated(formatNow());
        }

        return result;
      } finally {
        if (trackRefresh && mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [loadAnalytics, run],
  );

  const handleRefresh = useCallback(
    () => loadPageData({ trackRefresh: true }),
    [loadPageData],
  );

  useEffect(() => {
    mountedRef.current = true;
    loadPageData().catch(() => undefined);

    return () => {
      mountedRef.current = false;
    };
  }, [loadPageData]);

  useEffect(() => {
    registerPageControls({
      onRefresh: handleRefresh,
      refreshEvery: "Manual",
      lastUpdated,
      isRefreshing: shellRefreshing,
    });

    return () => {
      resetPageControls();
    };
  }, [
    handleRefresh,
    lastUpdated,
    registerPageControls,
    resetPageControls,
    shellRefreshing,
  ]);

  const callSeries = useMemo(
    () => data?.callsPerDay || { labels: [], values: [] },
    [data?.callsPerDay],
  );
  const durationSeries = useMemo(
    () => buildDurationBuckets(data?.rawCalls || []),
    [data?.rawCalls],
  );
  const outcomeSeries = useMemo(() => {
    const calls = data?.rawCalls || [];
    const booked = calls.filter((call) => {
      const disp = String(call.disposition || call.booking_status || "").toLowerCase();
      return (
        call.booking_confirmed ||
        disp === "booked" ||
        /^(converted|qualified|won|sale|successful)$/.test(disp)
      );
    }).length;
    const requested = calls.filter((call) => {
      const disp = String(call.disposition || call.booking_status || "").toLowerCase();
      return /^(callback_requested|interested|follow_up|requested)$/.test(disp);
    }).length;
    const dropped = calls.filter(
      (call) => Number(call.duration_seconds || 0) < 10,
    ).length;

    return buildOutcomeSeries({
      total_calls: calls.length,
      total_bookings: booked,
      total_booking_requests: requested,
      dropped_calls: dropped,
    });
  }, [data?.rawCalls]);
  const leadTaskSeries = useMemo(
    () => buildLeadTaskSeries(data?.leads || []),
    [data?.leads],
  );

  return (
    <>
      {error ? (
        <RetryBlock
          message="Data unavailable right now. Showing empty analytics."
          onRetry={() => handleRefresh().catch(() => undefined)}
        />
      ) : null}
      <AnalyticsCharts
        callSeries={callSeries}
        durationSeries={durationSeries}
        outcomeSeries={outcomeSeries}
        leadTaskSeries={leadTaskSeries}
      />
    </>
  );
}
